import { useMemo, useCallback, useRef, useEffect } from 'react';
import type { WithContext, ItemList, VideoGallery } from 'schema-dts';

import { StockItemDetails } from '../../common/types/StockItemTypes';
import {
  generateVideoCollectionSchema,
  clearSchemaCache,
} from '../utils/videoSchemaGenerator';

interface UseVideoGridSchemaProps {
  heading: string;
  description?: string;
  videos: StockItemDetails[];
  pageUrl?: string;
  contentMode?: 'search' | 'collection' | 'mixed';
  category?: string;
  searchTerm?: string;
  totalResults?: number;
  currentPage?: number;
  itemsPerPage?: number;
  enableCaching?: boolean;
  maxCacheSize?: number;
  autoInject?: boolean;
}

interface VideoGridSchemaResult {
  schema: string;
  parsedSchema?: WithContext<ItemList | VideoGallery>;
  isLoading: boolean;
  error: string | null;
  clearCache: () => void;
}

const schemaCache = new Map<string, string>();
const MAX_CACHE_SIZE = 100;

function simpleHash(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Enhanced hook for generating video grid JSON-LD schema with performance optimizations
 * Automatically injects schema into DOM for backward compatibility
 */
export const useVideoGridSchema = ({
  heading,
  description,
  videos,
  pageUrl,
  contentMode = 'mixed',
  category,
  searchTerm,
  totalResults,
  currentPage,
  itemsPerPage,
  enableCaching = true,
  maxCacheSize = MAX_CACHE_SIZE,
  autoInject = true,
}: UseVideoGridSchemaProps): VideoGridSchemaResult => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const schemaElementRef = useRef<HTMLScriptElement | null>(null);
  const lastSchemaRef = useRef<string>('');
  const componentIdRef = useRef<string>(
    `video-schema-${Math.random().toString(36).substr(2, 9)}`
  );

  const cacheKey = useMemo(() => {
    const videoIds = videos
      .map((v) => v.stockItem.id)
      .slice((currentPage - 1) * itemsPerPage, itemsPerPage)
      .join(',');
    const key = [
      heading,
      description,
      videoIds,
      pageUrl,
      contentMode,
      category,
      searchTerm,
      totalResults,
      currentPage,
      itemsPerPage,
      videos.length,
    ].join('|');
    return simpleHash(key).slice(0, 16);
  }, [
    heading,
    description,
    videos, // ideally, videos should be stable or you should depend on videos.map(v => v.stockItem.id).join(",")
    pageUrl,
    contentMode,
    category,
    searchTerm,
    totalResults,
    currentPage,
    itemsPerPage,
  ]);

  const manageCacheSize = useCallback(() => {
    if (schemaCache.size > maxCacheSize) {
      const keysToDelete = Array.from(schemaCache.keys()).slice(
        0,
        schemaCache.size - maxCacheSize + 10
      );
      keysToDelete.forEach((key) => schemaCache.delete(key));
    }
  }, [maxCacheSize]);

  const clearCache = useCallback(() => {
    schemaCache.clear();
    clearSchemaCache();
  }, []);

  const updateSchema = useCallback(
    (newSchema: string) => {
      if (!autoInject || !newSchema || newSchema === lastSchemaRef.current) {
        return;
      }

      if (schemaElementRef.current) {
        schemaElementRef.current.textContent = newSchema;
      } else {
        const schemaElement = document.createElement('script');
        schemaElement.type = 'application/ld+json';
        schemaElement.textContent = newSchema;
        schemaElement.setAttribute(
          'data-video-grid-schema',
          componentIdRef.current
        );

        document.head.appendChild(schemaElement);
        schemaElementRef.current = schemaElement;
      }

      lastSchemaRef.current = newSchema;
    },
    [autoInject]
  );

  const generateSchema = useCallback((): string => {
    if (!videos?.length) {
      return '';
    }

    if (enableCaching && schemaCache.has(cacheKey)) {
      return schemaCache.get(cacheKey)!;
    }

    try {
      const currentPageUrl =
        pageUrl ||
        (typeof window !== 'undefined' ? window.location.href : undefined);

      const schema = generateVideoCollectionSchema({
        heading,
        description,
        videos,
        pageUrl: currentPageUrl,
        contentMode,
        category,
        searchTerm,
        totalResults,
        currentPage,
        itemsPerPage,
      });

      if (enableCaching && schema) {
        schemaCache.set(cacheKey, schema);
        manageCacheSize();
      }

      return schema;
    } catch (error) {
      return '';
    }
  }, [
    videos,
    cacheKey,
    enableCaching,
    heading,
    description,
    pageUrl,
    contentMode,
    category,
    searchTerm,
    totalResults,
    currentPage,
    itemsPerPage,
    manageCacheSize,
  ]);

  const schema = useMemo(() => {
    return generateSchema();
  }, [generateSchema]);

  // Parse schema for advanced use cases
  const parsedSchema = useMemo(():
    | WithContext<ItemList | VideoGallery>
    | undefined => {
    if (!schema) return undefined;
    try {
      return JSON.parse(schema) as WithContext<ItemList | VideoGallery>;
    } catch {
      return undefined;
    }
  }, [schema]);

  // Force re-generation when videos change (important for loading states)
  useEffect(() => {
    if (videos?.length > 0) {
      if (schemaCache.has(cacheKey)) {
        schemaCache.delete(cacheKey);
      }
    }
  }, [videos, cacheKey]);

  useEffect(() => {
    if (schema && schema.length > 0 && videos?.length > 0 && autoInject) {
      updateSchema(schema);
    } else if (schemaElementRef.current && autoInject) {
      schemaElementRef.current.remove();
      schemaElementRef.current = null;
      lastSchemaRef.current = '';
    }
  }, [schema, updateSchema, autoInject, videos]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (schemaElementRef.current && autoInject) {
        schemaElementRef.current.remove();
        schemaElementRef.current = null;
        lastSchemaRef.current = '';
      }
    };
  }, [autoInject]);

  useEffect(() => {
    const interval = setInterval(() => {
      manageCacheSize();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return {
    schema,
    parsedSchema,
    isLoading: false,
    error: null,
    clearCache,
  };
};
